import React, { FunctionComponent, useState, useEffect, useCallback, useMemo } from 'react';

import classnames from 'classnames';
import _ from 'lodash';
import { Button, Dropdown, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';
import { AutoSizer, Grid } from 'react-virtualized';

import { ActionId } from 'admin/actions/Actions';
import { GroupId } from 'admin/groups/Groups';
import { ModuleId } from 'admin/modules/Modules';
import { useProfile, isAuthorized } from 'auth/Auth';
import { FilterBar } from 'common/filters/filter-bar/FilterBar';
import { useFilters } from 'common/filters/Filters';
import { ObjectModel } from 'common/filters/Filters.types';
import { Overlay } from 'core/overlay/Overlay';
import { Size } from 'core/util/Bootstrap';
import { Route } from 'core/util/Route';
import { route } from 'core/util/Routing';
import { ErrorDisplay } from 'social/publisher/error-display/ErrorDisplay';
import {
    LocationUid,
    LocationSource,
    SelectedSource,
    SourceName,
    SourceStatus,
    PublisherError,
    EnabledSource,
    LocationGroup,
    EnabledLocationGroup,
} from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import { fetchLocations } from 'social/publisher/PublisherFetchers';
import { getSourceKeysFromSelectedSources, getSourcesAvailable } from 'social/publisher/util/Util';

import 'social/publisher/location-picker/LocationPicker.scss';
import 'react-virtualized/styles.css';

interface Props {
    locationSources: LocationSource[];
    selectedLocations: LocationUid[];
    selectedSources: SelectedSource;
    isSingleLocationUser: boolean;
    publisherErrors: PublisherError[];
    isLocationsLocked: boolean;
    enabledSources: EnabledSource;
    isLibraryContentCreateMode: boolean;
    selectedLocationGroup?: LocationGroup;
    enabledLocationGroups?: EnabledLocationGroup;
}

interface LocationDisplay {
    lCode: string | undefined;
    internalName: string;
    lUid: LocationUid;
    connections: SourceStatus;
    searchValue: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface LocationUidBySource extends Record<SourceName, LocationUid[]> {
    [index: string]: LocationUid[];
}

export const LocationPicker: FunctionComponent<Props> = ({
    locationSources,
    selectedLocations,
    selectedSources,
    isSingleLocationUser,
    publisherErrors,
    isLocationsLocked,
    enabledSources,
    isLibraryContentCreateMode,
    selectedLocationGroup,
    enabledLocationGroups,
}) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation(['social/publisher']);
    // Facet filters used in locations modal
    const [{ selectedFilters }] = useFilters();
    const filterBarOptions = {
        objectModel: ObjectModel.EntityMetadata,
        groupID: GroupId.Social,
        moduleIDs: [ModuleId.Social2],
        persist: false,
        selectedFilters: [{ name: 'entityMetadata.location', values: [] }],
    };
    // User auth profile
    const [{ profile }] = useProfile();
    // Internal state of the location modal display
    const [isModalShowing, setIsModalShowing] = useState<boolean>(false);
    // Internal state of the whether we're filtering by only the selected locations in the list or not
    const [isShowSelectedOnly, setIsShowSelectedOnly] = useState<boolean>(false);
    // Internal state of the location rows derived from the available locations, authorized sources, and selectedLocations
    const [locationsDisplay, setLocationsDisplay] = useState<LocationDisplay[]>([]);
    // A cloned version of the locationsDisplay that can be used to swap views temporarily
    const [cachedLocationsDisplay, setCachedLocationsDisplay] = useState<LocationDisplay[]>([]);
    // Internal state of the selected location before dispatching to reducer
    const [locationsSelected, setLocationsSelected] = useState<LocationUid[]>([]);
    const [locationSearchQuery, setLocationSearchQuery] = useState('');
    const shouldOnlySelectOneLocation =
        (selectedSources.TWITTER || selectedSources.LINKEDIN) && !isLibraryContentCreateMode;
    const sourcesAvailable = isLibraryContentCreateMode
        ? enabledSources
        : getSourcesAvailable(enabledSources, locationSources);
    const [numSelectedSources, setNumSelectedSources] = useState<number>(0);

    // Automatically popup the modal if a source has changed to a "single location only" source and the user had previously selected locations
    useEffect(() => {
        if (shouldOnlySelectOneLocation && selectedLocations.length > 1) {
            const newSelectedLocations: LocationUid[] = [];

            setLocationsSelected(newSelectedLocations);
            dispatch({
                type: 'set-selected-locations',
                payload: {
                    value: newSelectedLocations,
                },
            });
            setIsModalShowing(true);
        }
    }, [shouldOnlySelectOneLocation, selectedLocations, dispatch]);
    const getLocationPickerHeader = (): JSX.Element => {
        let header;

        if (selectedSources.TWITTER || selectedSources.LINKEDIN) {
            let tooltip = 'SELECT_LOCATIONS_LINKEDIN';

            if (selectedSources.TWITTER && selectedSources.LINKEDIN) {
                tooltip = 'SELECT_LOCATIONS_TWITTER_AND_LINKEDIN';
            } else if (selectedSources.TWITTER) {
                tooltip = 'SELECT_LOCATIONS_TWITTER';
            }
            header = (
                <span>
                    {t('SELECT_ONE_LOCATION')}
                    <OverlayTrigger
                        placement="right"
                        overlay={<Tooltip id="location-picker-header-tooltip">{t(tooltip)}</Tooltip>}
                    >
                        <i className="icon-info-circle location-picker-header-icon" />
                    </OverlayTrigger>
                </span>
            );
        } else {
            header = <span>{t('SELECT_LOCATIONS')}</span>;
        }
        return header;
    };
    const arrayContainsArray = (superset: number[], subset: number[]): boolean => {
        if (subset.length === 0) {
            return false;
        }
        return subset.every((value) => superset.indexOf(value) >= 0);
    };
    // Builds a LocationDisplay object from a LocationSource object, used for consistent mapping
    const locationDisplayBuilder = (loc: LocationSource): LocationDisplay => {
        return {
            lUid: loc.lUid,
            lCode: loc.lCode,
            internalName: loc.lName,
            connections: {
                FACEBOOK: loc.sources.FACEBOOK,
                TWITTER: loc.sources.TWITTER,
                INSTAGRAM: loc.sources.INSTAGRAM,
                GOOGLE_PLACES: loc.sources.GOOGLE_PLACES,
                LINKEDIN: loc.sources.LINKEDIN,
                YOUTUBE: loc.sources.YOUTUBE,
                IG_STORIES: loc.sources.INSTAGRAM, // IG Stories works if IG is connected
                IG_REELS: loc.sources.INSTAGRAM, // IG Reels works if IG is connected
                TIKTOK: loc.sources.TIKTOK,
            },
            searchValue: `${loc.lUid} ${loc.lCode} ${loc.lName}`,
        };
    };
    const handleShowModal = (): void => {
        // Reset the viewstate'f locationsSelected back to the original props
        setLocationsSelected(selectedLocations);
        setIsModalShowing(true);
    };
    const handleCloseModal = (): void => {
        // Reset the viewstate's locationsSelected back to the original props
        setLocationsSelected(selectedLocations);
        setIsModalShowing(false);

        // Resets the Location errors
        dispatch({ type: 'remove-publisher-errors-by-component', payload: { value: 'LOCATION' } });
    };
    const handleSaveModal = (): void => {
        dispatch({
            type: 'set-selected-locations',
            payload: {
                value: locationsSelected,
            },
        });
        dispatch({
            type: 'set-has-user-selected-locations-manually',
            payload: {
                value: true,
            },
        });
        setIsModalShowing(false);

        // Resets the Location errors
        dispatch({ type: 'remove-publisher-errors-by-component', payload: { value: 'LOCATION' } });
    };
    // Handles deseletion of all locations
    // const handleClearAllSelections = (): void => {
    //     setLocationsSelected([]);
    //     setLocationsDisplay(cachedLocationsDisplay);
    //     setIsShowSelectedOnly(false);
    // };
    // const handleShowSelectedOnly = (): void => {
    //     if (isShowSelectedOnly) {
    //         setLocationsDisplay(cachedLocationsDisplay);
    //         setIsShowSelectedOnly(false);
    //     } else {
    //         const newLocations = locationsDisplay.filter((loc) => locationsSelected.indexOf(loc.lUid) !== -1);

    //         setLocationsDisplay(newLocations);
    //         setIsShowSelectedOnly(true);
    //     }
    // };
    // Handles selection of all connected locations based on selected sources
    const handleSelectAllConnectedLocations = (): void => {
        const connectedLocationUids: LocationUid[] = [];

        locationsDisplay.forEach((locationDisplay: LocationDisplay) => {
            Object.keys(locationDisplay.connections).forEach((connectionKey) => {
                if (
                    locationDisplay.connections[connectionKey] !== undefined &&
                    selectedSources[connectionKey] &&
                    !connectedLocationUids.includes(locationDisplay.lUid)
                ) {
                    connectedLocationUids.push(locationDisplay.lUid);
                }
            });
        });
        setLocationsSelected(connectedLocationUids);
    };
    const handleSearchLocation = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setLocationSearchQuery(e.target.value);
        const query = e.target.value.toLowerCase();

        if (isShowSelectedOnly) {
            const matchedLocations = cachedLocationsDisplay.filter(
                (loc) =>
                    locationsSelected.indexOf(loc.lUid) !== -1 && loc.searchValue.toLowerCase().indexOf(query) > -1,
            );

            setLocationsDisplay(matchedLocations);
        } else {
            const matchedLocations = cachedLocationsDisplay.filter(
                (loc) => loc.searchValue.toLowerCase().indexOf(query) > -1,
            );

            setLocationsDisplay(matchedLocations);
        }
    };
    // Fetches and sets internal locations to display based on the Facets filter
    const fetchAndSetFilteredLocations = async (): Promise<void> => {
        let filteredLocations;

        /* This avoids unncessary calls/rerenders if the modal isn't showing,
         * and doesn't load locations on compoonentDidMount (via useEffect hook)
         * since we won't be showing it
         */
        if (!isModalShowing) {
            return;
        }

        try {
            filteredLocations = await trackPromise(
                fetchLocations(profile?.tenant?.agencyID!, profile?.tenant?.id, selectedFilters),
                'fetch-locations',
            );
        } catch {}

        if (filteredLocations) {
            const mappedLocations: LocationDisplay[] = [];

            _.forEach(filteredLocations, (loc, index) => {
                const matchedLocation = _.find(locationSources, { lUid: loc.uid });

                // The location didn't exist in our locationSources
                if (!matchedLocation) {
                    return;
                }

                mappedLocations.push(locationDisplayBuilder(matchedLocation));
            });
            setLocationsDisplay(mappedLocations);

            // Deselect any locations no longer available from the facet filter
            const newLocationsSelected: LocationUid[] = [];

            locationsSelected.forEach((lUid) => {
                const matchedLocation = _.find(mappedLocations, { lUid });

                if (matchedLocation) {
                    newLocationsSelected.push(lUid);
                }
            });
            setLocationsSelected(newLocationsSelected);
            // Additional guards when filters are applied to reset these views
            setCachedLocationsDisplay(mappedLocations);
        } else {
            // A fallback to show all the available locations if the fetchLocations service fails
            const initialLocations: LocationDisplay[] = locationSources.map(locationDisplayBuilder);

            setLocationsDisplay(initialLocations);
        }

        setIsShowSelectedOnly(false);
    };
    // Wrapped definition for fetchAndSetFilteredLocations
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchAndSetFilteredLocationsWrapper = useCallback(fetchAndSetFilteredLocations, [selectedFilters]);

    // Handles changes of facet Filter
    useEffect((): void => {
        fetchAndSetFilteredLocationsWrapper();
        setIsShowSelectedOnly(false);
    }, [fetchAndSetFilteredLocationsWrapper, locationSources]);

    // Determine the text for the button that launches the locations modal
    const displayModalLauncherText = (): JSX.Element => {
        let modalLauncherText = <></>;

        if (selectedLocations.length > 0) {
            if (selectedLocations.length === 1) {
                const matchedLocation = locationSources.filter((loc) => {
                    return loc.lUid === selectedLocations[0];
                })[0];

                if (matchedLocation) {
                    modalLauncherText = <>{matchedLocation.lName}</>;
                }
            } else {
                modalLauncherText = <>{t('COUNT_LOCATIONS_SELECTED', { count: selectedLocations.length })}</>;
            }
        } else {
            modalLauncherText = <>{t('SELECT_LOCATIONS')}</>;
        }

        return modalLauncherText;
    };
    // Dropdown button text for All vs My vs Select locations
    const displayLocationDropdownText = (): JSX.Element => {
        let locationDropdownText = <></>;

        switch (selectedLocationGroup) {
            case 'ALL_LOCATIONS':
                locationDropdownText = <>{t('ALL_LOCATIONS')}</>;
                break;
            case 'MY_LOCATIONS':
                locationDropdownText = <>{t('MY_LOCATIONS')}</>;
                break;
            case 'MY_LOCATION':
                locationDropdownText = <>{t('MY_LOCATION')}</>;
                break;
            case 'SELECT_LOCATIONS':
                locationDropdownText = displayModalLauncherText();
                break;
            default:
                locationDropdownText = <>{t('ALL_LOCATIONS')}</>;
                break;
        }
        return locationDropdownText;
    };
    const handleSelectLocationGroup = (selectedLocationGroup: LocationGroup): void => {
        dispatch({
            type: 'set-selected-location-group',
            payload: {
                value: selectedLocationGroup,
            },
        });
    };
    // Builds the locations header
    const displayHeader = useMemo(() => {
        // const locationLuidBySource: LocationUidBySource = {
        //     FACEBOOK: getLuidBySource(locationSources, 'FACEBOOK'),
        //     INSTAGRAM: getLuidBySource(locationSources, 'INSTAGRAM'),
        //     TWITTER: getLuidBySource(locationSources, 'TWITTER'),
        //     GOOGLE_PLACES: getLuidBySource(locationSources, 'GOOGLE_PLACES'),
        //     LINKEDIN: getLuidBySource(locationSources, 'LINKEDIN'),
        //     YOUTUBE: getLuidBySource(locationSources, 'YOUTUBE'),
        // };

        const getLocationsDiplayCountSource = (locationsDisplay: LocationDisplay[], sourceName: SourceName): number => {
            return locationsDisplay.filter((loc) => {
                return (
                    loc.connections[sourceName] === 'VALID' ||
                    loc.connections[sourceName] === 'INVALID' ||
                    loc.connections[sourceName] === 0 ||
                    loc.connections[sourceName] === 1
                );
            }).length;
        };

        // const isAllOfSourceLocationsSelected = (sourceName: SourceName): boolean => {
        //     const displayLocationUids = locationsDisplay.map((loc) => loc.lUid);
        //     const authArr: LocationUid[] = locationLuidBySource[sourceName];
        //     const sourceDisplayLocationUids: LocationUid[] = _.intersectionWith(
        //         displayLocationUids,
        //         authArr,
        //         _.isEqual,
        //     );

        //     if (arrayContainsArray(locationsSelected, sourceDisplayLocationUids)) {
        //         return true;
        //     }
        //     return false;
        // };
        const isAllVisibleLocationsSelected = (): boolean => {
            const displayLocationUids = locationsDisplay.map((loc) => loc.lUid);

            if (arrayContainsArray(locationsSelected, displayLocationUids)) {
                return true;
            }
            return false;
        };
        // const handleSelectAllVisibleLocationsBySource = (
        //     e: React.FormEvent<HTMLInputElement>,
        //     sourceName: SourceName,
        // ): void => {
        //     const newLocations: LocationUid[] = [...locationsSelected];
        //     const displayLocationUids: LocationUid[] = locationsDisplay.map((val) => val.lUid);
        //     const authArr: LocationUid[] = locationLuidBySource[sourceName];
        //     const sourceDisplayLocationUids: LocationUid[] = _.intersectionWith(
        //         displayLocationUids,
        //         authArr,
        //         _.isEqual,
        //     );

        //     if (isAllOfSourceLocationsSelected(sourceName)) {
        //         setLocationsSelected(_.difference(newLocations, sourceDisplayLocationUids));
        //     } else {
        //         setLocationsSelected(_.uniq(newLocations.concat(sourceDisplayLocationUids)));
        //     }
        // };
        const handleSelectAllVisibleLocations = (e: React.FormEvent<HTMLInputElement>): void => {
            const newLocations: LocationUid[] = [...locationsSelected];
            const displayLocationUids: LocationUid[] = locationsDisplay.map((val) => val.lUid);

            if (isAllVisibleLocationsSelected()) {
                setLocationsSelected(_.difference(newLocations, displayLocationUids));
            } else {
                setLocationsSelected(_.uniq(newLocations.concat(displayLocationUids)));
            }
        };
        const isSelectAllLocationsDisabled =
            locationSources.length === 0 || (shouldOnlySelectOneLocation && locationSources.length > 1);
        // const isSelectAllOfSourceLocationsDisabled = (sourceName: SourceName): boolean => {
        //     return (
        //         (shouldOnlySelectOneLocation && locationLuidBySource[sourceName].length > 1) ||
        //         (shouldOnlySelectOneLocation && locationsSelected.length > 0)
        //     );
        // };

        return (
            <>
                <div className="location-grid-header-check">
                    <Form.Check
                        className="location-grid-header-check-all"
                        type="checkbox"
                        disabled={isSelectAllLocationsDisabled}
                        checked={isAllVisibleLocationsSelected()}
                        onChange={(e): void => handleSelectAllVisibleLocations(e)}
                    />
                </div>
                <div className="location-grid-header-name">
                    {t('LOCATION_NAME_HEADER', { count: locationsSelected.length })}
                </div>
                <div className="location-grid-header-sources">
                    {getSourceKeysFromSelectedSources(sourcesAvailable).map((el) => {
                        if (el !== 'IG_STORIES' && el !== 'IG_REELS') {
                            return (
                                <div className="location-grid-header-source" key={el}>
                                    {/*
                                <Form.Check
                                    className="location-grid-header-source-check"
                                    type="checkbox"
                                    checked={isAllOfSourceLocationsSelected('FACEBOOK')}
                                    onChange={(e): void => handleSelectAllVisibleLocationsBySource(e, 'FACEBOOK')}
                                    disabled={isSelectAllOfSourceLocationsDisabled('FACEBOOK')}
                                />
                                */}
                                    <div className="location-grid-header-source-check" />
                                    <div
                                        className={`location-grid-header-source-icon social-icon ${el.toLowerCase()}`}
                                    />
                                    <div className="location-grid-header-source-count">
                                        <span>{`(${getLocationsDiplayCountSource(locationsDisplay, el)})`}</span>
                                    </div>
                                </div>
                            );
                            // eslint-disable-next-line no-else-return
                        } else {
                            return <></>;
                        }
                    })}
                </div>
            </>
        );
    }, [locationsDisplay, locationsSelected, locationSources, shouldOnlySelectOneLocation, t, sourcesAvailable]);
    // Builds the locations grid
    const displayList = useMemo(() => {
        // Handles selection or deselection of a single location by lUid
        const handleSelectLocation = (e: React.FormEvent<HTMLInputElement>, lUid: LocationUid): void => {
            const newLocations: LocationUid[] = [...locationsSelected];

            if (e.currentTarget.checked === true) {
                newLocations.push(lUid);
            } else {
                const index = _.indexOf(newLocations, lUid);

                if (index >= 0) {
                    newLocations.splice(index, 1);
                }
            }
            setLocationsSelected(newLocations);
        };

        const buildLocationRow = (loc: LocationDisplay): JSX.Element[] => {
            const locationRow: JSX.Element[] = [];
            const sourceGrid: JSX.Element[] = [];
            const inputId = `location-${loc.lUid}`;
            const connectedIcon = (
                <OverlayTrigger
                    key={`${loc.lUid}`}
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${loc.lUid}`}>{t('READY_TO_PUBLISH')}</Tooltip>}
                >
                    <i className="icon-globe connected" />
                </OverlayTrigger>
            );
            // TODO: This will have to have a new design, one that communicates something less urgent than a red (!)
            const disconnectedIcon = (sourceName: SourceName) => (
                <OverlayTrigger
                    key={`${loc.lUid}`}
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${loc.lUid}`}>{t('TOKEN_NOT_FOUND')}</Tooltip>}
                >
                    {isAuthorized({ actions: [ActionId.PlatformCredentials.View] }) ? (
                        <a
                            href={route(
                                `${Route.Admin}/agencies/${profile?.tenant?.agencyID!}/tenants/${
                                    profile?.tenant?.id
                                }/credentials/sources/${sourceName}`,
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <i className="icon-exclamation-circle disconnected" />
                        </a>
                    ) : (
                        <i className="icon-exclamation-circle disconnected" />
                    )}
                </OverlayTrigger>
            );
            const pagelessIcon = (sourceName: SourceName) => (
                <OverlayTrigger
                    key={`${loc.lUid}`}
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${loc.lUid}`}>{t('NO_PAGE_CONNECTED')}</Tooltip>}
                >
                    {isAuthorized({ actions: [ActionId.PlatformCredentials.View] }) ? (
                        <a
                            href={route(
                                `${Route.Admin}/agencies/${profile?.tenant?.agencyID!}/tenants/${
                                    profile?.tenant?.id
                                }/credentials/sources/${sourceName}`,
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <i className="icon-exclamation-circle pageless" />
                        </a>
                    ) : (
                        <i className="icon-exclamation-circle pageless" />
                    )}
                </OverlayTrigger>
            );
            let isSelected = false;

            // Check if the location is selected by user
            if (_.indexOf(locationsSelected, loc.lUid) !== -1) {
                isSelected = true;
            }

            // Builds the source grid for this location
            _.forEach(loc.connections, (sourceConnection, sourceName) => {
                if (sourcesAvailable[sourceName] && sourceName !== 'IG_STORIES' && sourceName !== 'IG_REELS') {
                    let icon;

                    if (sourceConnection === 'VALID' || sourceConnection === 0) {
                        icon = connectedIcon;
                    } else if (sourceConnection === 'INVALID' || sourceConnection === 1) {
                        icon = disconnectedIcon(sourceName as SourceName);
                    } else {
                        icon = pagelessIcon(sourceName as SourceName);
                    }
                    sourceGrid.push(<div key={`${loc.lUid}-${sourceName}`}>{icon}</div>);
                }
            });

            const controlCol = (
                <div>
                    <Form.Check
                        id={inputId}
                        className="location-grid-row-check"
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e): void => {
                            handleSelectLocation(e, loc.lUid);
                        }}
                        disabled={shouldOnlySelectOneLocation && locationsSelected.length > 0 && !isSelected}
                    />
                </div>
            );
            const locationNameCol = (
                <Form.Label htmlFor={inputId} className={classnames('location-grid-row-location')}>
                    <span>
                        {loc.internalName} ({loc.lCode})
                    </span>
                </Form.Label>
            );
            const sourceGridCol = <div className="location-grid-row-sources">{sourceGrid}</div>;

            locationRow.push(controlCol);
            locationRow.push(locationNameCol);
            locationRow.push(sourceGridCol);

            return locationRow;
        };

        const selectedLocationRows: JSX.Element[][] = locationsDisplay
            .filter((loc) => _.includes(selectedLocations, loc.lUid))
            .map(buildLocationRow);

        const unselectedLocationRows: JSX.Element[][] = locationsDisplay
            .filter((loc) => !_.includes(selectedLocations, loc.lUid))
            .map(buildLocationRow);

        return selectedLocationRows.concat(unselectedLocationRows);
    }, [
        t,
        locationsDisplay,
        locationsSelected,
        shouldOnlySelectOneLocation,
        sourcesAvailable,
        selectedLocations,
        profile,
    ]);
    // Virtualized Grid display values, in pixels
    const GRID_WIDTH = 1108;
    const GRID_HEIGHT = 380;
    const ROW_HEIGHT = 55;
    const COLUMN_COUNT = 3;
    const getGridColumnWidth = ({ index }: { index: number }): number => {
        switch (index) {
            // Checkbox
            case 0:
                return 50;
            // Location Name
            case 1:
                return 418;
            // Source Grid
            case 2:
                return 640;
            default:
                return 100;
        }
    };
    const gridCellRenderer = ({
        columnIndex,
        key,
        rowIndex,
        style,
    }: {
        columnIndex: number;
        key: string;
        rowIndex: number;
        style: any;
    }): JSX.Element => {
        return (
            <div key={key} style={style}>
                {displayList[rowIndex][columnIndex]}
            </div>
        );
    };
    let displayGrid;

    if (locationsDisplay.length === 0) {
        displayGrid = (
            <div className="location-grid-content-empty">
                <Overlay area="fetch-locations" />
                {t('NO_LOCATIONS_TO_DISPLAY')}
            </div>
        );
    } else {
        displayGrid = (
            <div className="location-grid-content-loaded">
                <Overlay area="fetch-locations" />
                <AutoSizer>
                    {({ height, width }): JSX.Element => (
                        <Grid
                            cellRenderer={gridCellRenderer}
                            columnCount={COLUMN_COUNT}
                            columnWidth={getGridColumnWidth}
                            height={GRID_HEIGHT}
                            rowCount={displayList.length}
                            rowHeight={ROW_HEIGHT}
                            width={GRID_WIDTH}
                        />
                    )}
                </AutoSizer>
            </div>
        );
    }
    const singleLocation: LocationSource | undefined = isSingleLocationUser ? locationSources[0] : undefined;
    const singleLocationUid = singleLocation?.lUid;

    // Logic to automatically set the location if the user only has access to a single location
    useEffect(() => {
        if (isSingleLocationUser && !isLocationsLocked) {
            dispatch({
                type: 'set-selected-locations',
                payload: {
                    value: [singleLocationUid],
                },
            });
        }
    }, [isSingleLocationUser, singleLocationUid, dispatch, isLocationsLocked]);

    // Logic to handle managing the "Show selected locations only" checkbox and the locations to display when all locations become unselected
    useEffect((): void => {
        if (locationsSelected.length === 0 && isShowSelectedOnly) {
            setLocationsDisplay(cachedLocationsDisplay);
            setIsShowSelectedOnly(false);
        }
    }, [locationsSelected, isShowSelectedOnly, cachedLocationsDisplay]);

    useEffect(() => {
        let num = 0;

        Object.keys(selectedSources).forEach((source) => {
            if (selectedSources[source]) {
                num += 1;
            }
        });

        setNumSelectedSources(num);
    }, [selectedSources]);

    return (
        <div className="r4e-social-location-picker">
            <h3 className="location-picker-title">{t('SELECT_LOCATIONS')}</h3>
            <ErrorDisplay publisherErrors={publisherErrors} component="LOCATION" />
            {isLibraryContentCreateMode && enabledLocationGroups ? (
                <Dropdown drop="down">
                    <Dropdown.Toggle variant="default" id="location-options-dropdown" disabled={isLocationsLocked}>
                        <i className="icon icon-map-marker-alt" />
                        <span>{displayLocationDropdownText()}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {enabledLocationGroups.ALL_LOCATIONS && (
                            <Dropdown.Item onClick={(): void => handleSelectLocationGroup('ALL_LOCATIONS')}>
                                {t('ALL_LOCATIONS')}
                            </Dropdown.Item>
                        )}
                        {enabledLocationGroups.MY_LOCATIONS && (
                            <Dropdown.Item onClick={(): void => handleSelectLocationGroup('MY_LOCATIONS')}>
                                {t('MY_LOCATIONS')}
                            </Dropdown.Item>
                        )}
                        {enabledLocationGroups.MY_LOCATION && (
                            <Dropdown.Item onClick={(): void => handleSelectLocationGroup('MY_LOCATION')}>
                                {t('MY_LOCATION')}
                            </Dropdown.Item>
                        )}
                        {enabledLocationGroups.SELECT_LOCATIONS && (
                            <Dropdown.Item
                                onClick={(): void => {
                                    handleSelectLocationGroup('SELECT_LOCATIONS');
                                    handleShowModal();
                                }}
                            >
                                {t('SELECT_LOCATIONS')}
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            ) : (
                <Button
                    onClick={(): void => {
                        if (isSingleLocationUser || isLocationsLocked) {
                            return;
                        }
                        handleShowModal();
                    }}
                    variant="default"
                    className={classnames('btn-locations', { disabled: isSingleLocationUser || isLocationsLocked })}
                >
                    <i className="icon icon-map-marker-alt" />
                    <span>{displayModalLauncherText()}</span>
                </Button>
            )}
            <Modal
                show={isModalShowing}
                onHide={handleCloseModal}
                className="r4e-social-location-picker-modal"
                backdrop="static"
                size={Size.XLarge}
                keyboard
            >
                <Modal.Header closeButton>
                    <Modal.Title>{getLocationPickerHeader()}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FilterBar filterBarOptions={filterBarOptions} />
                    <div className="location-header">
                        {/* <div className="location-search-input"> */}
                        <Form.Control
                            type="text"
                            placeholder={t('SEARCH_LOCATIONS_PLACEHOLDER')}
                            value={locationSearchQuery}
                            onChange={handleSearchLocation}
                        />
                        {/* </div> */}
                    </div>
                    <div className="location-grid-header">{displayHeader}</div>
                    <div className="location-grid-content">{displayGrid}</div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="modal-footer-options">
                        {/* <Button variant="default" onClick={handleClearAllSelections} className="btn-clear">
                            {t('CLEAR_ALL')}
                        </Button> */}
                        {/* <Form.Check
                            className="check-selected-only"
                            onChange={handleShowSelectedOnly}
                            type="checkbox"
                            label={t('TOGGLE_SELECTED_LOCATIONS_ONLY')}
                            disabled={locationsSelected.length === 0}
                            checked={isShowSelectedOnly}
                        /> */}
                        {!shouldOnlySelectOneLocation && numSelectedSources > 0 && (
                            <Button variant="default" onClick={handleSelectAllConnectedLocations} className="btn-clear">
                                {t('SELECT_ALL_CONNECTED_LOCATIONS')}
                            </Button>
                        )}
                    </div>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        {t('CANCEL')}
                    </Button>
                    <Button variant="primary" onClick={handleSaveModal}>
                        {t('SELECT')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
